import { inject, Injectable, signal } from '@angular/core';
import { BrowserStorageService } from '@pf/shared-services';

const STORAGE_KEY = 'pf:printer-preferences';

export interface PrinterPreferences {
    printerAddress: string;
    printerUid: string;
    copies: number;
    remember: boolean;
    autoPrint: boolean;
}

const defaultPreferences: PrinterPreferences = {
    printerAddress: '',
    printerUid: '',
    copies: 1,
    remember: false,
    autoPrint: false,
};

@Injectable({
    providedIn: 'root',
})
export class PrinterPreferencesService {
    private readonly storageService = inject(BrowserStorageService);
    private readonly preferences = signal<PrinterPreferences>(this.read());
    readonly preferences$ = this.preferences.asReadonly();

    get(): PrinterPreferences {
        return this.preferences();
    }

    save(preferences: PrinterPreferences): void {
        this.preferences.set(preferences);
        if (preferences.remember) {
            this.storageService.setItem(STORAGE_KEY, preferences, 'local');
        } else {
            this.storageService.removeItem(STORAGE_KEY, 'local');
        }
    }

    clear(): void {
        this.preferences.set({ ...defaultPreferences });
        this.storageService.removeItem(STORAGE_KEY, 'local');
    }

    hasPrinter(): boolean {
        const prefs = this.preferences();
        return !!(prefs.printerAddress || prefs.printerUid);
    }

    private read(): PrinterPreferences {
        const stored = this.storageService.getItem<Partial<PrinterPreferences>>(
            STORAGE_KEY,
            'local'
        );
        if (!stored) {
            return { ...defaultPreferences };
        }
        return {
            ...defaultPreferences,
            ...stored,
        };
    }
}
